// Perfect-play rules from https://www.wikihow.com/Win-at-Tic-Tac-Toe
// Corner first when playing first. Center when answering a corner or an edge.
// Win and block before any setup move, then fork, then stop the opponent's fork.

const LINES = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  [1, 4, 7],
  [2, 5, 8],
  [3, 6, 9],
  [1, 5, 9],
  [3, 5, 7],
];

const CORNERS = [1, 3, 7, 9];
const EDGES = [2, 4, 6, 8];
const OPPOSITE = { 1: 9, 3: 7, 7: 3, 9: 1, 2: 8, 4: 6, 6: 4, 8: 2 };
const BETWEEN = {
  "1,3": 2,
  "1,7": 4,
  "1,9": 5,
  "3,7": 5,
  "3,9": 6,
  "7,9": 8,
};

function other(symbol) {
  return symbol === "cross" ? "circle" : "cross";
}

function empties(marks) {
  const cells = [];
  for (let i = 1; i <= 9; i++) if (!marks[i]) cells.push(i);
  return cells;
}

function cellsOf(marks, symbol) {
  const cells = [];
  for (let i = 1; i <= 9; i++) if (marks[i] === symbol) cells.push(i);
  return cells;
}

function between(a, b) {
  const key = a < b ? `${a},${b}` : `${b},${a}`;
  return BETWEEN[key] || null;
}

export function hasWin(marks, symbol) {
  return LINES.some((line) => line.every((cell) => marks[cell] === symbol));
}

function threatSquares(marks, symbol) {
  const found = [];
  LINES.forEach((line) => {
    let mine = 0;
    let emptyAt = 0;
    let blanks = 0;
    line.forEach((cell) => {
      if (marks[cell] === symbol) mine += 1;
      else if (!marks[cell]) {
        blanks += 1;
        emptyAt = cell;
      }
    });
    if (mine === 2 && blanks === 1 && !found.includes(emptyAt)) found.push(emptyAt);
  });
  return found;
}

function forkSquares(marks, symbol) {
  return empties(marks).filter((cell) => {
    const next = { ...marks, [cell]: symbol };
    return threatSquares(next, symbol).length >= 2;
  });
}

// A move that makes two in a row, so the opponent must answer,
// and that answer does not hand them a fork.
function forcingBlocks(marks, bot) {
  const opponent = other(bot);
  return empties(marks).filter((cell) => {
    const next = { ...marks, [cell]: bot };
    const threats = threatSquares(next, bot);
    if (threats.length !== 1) return false;
    const blocked = { ...next, [threats[0]]: opponent };
    if (hasWin(blocked, opponent)) return false;
    // Their answer must not already be a double threat. One threat is fine;
    // we answer it on the next move.
    return threatSquares(blocked, opponent).length < 2;
  });
}

export function candidateMoves(marks, bot) {
  const opponent = other(bot);
  if (hasWin(marks, bot) || hasWin(marks, opponent)) return [];

  const wins = threatSquares(marks, bot);
  if (wins.length) return wins;

  const blocks = threatSquares(marks, opponent);
  if (blocks.length) return blocks;

  const forks = forkSquares(marks, bot);
  if (forks.length) return forks;

  const opponentForks = forkSquares(marks, opponent);
  if (opponentForks.length === 1) return opponentForks;
  if (opponentForks.length > 1) {
    const forcing = forcingBlocks(marks, bot);
    if (forcing.length) return forcing;
  }

  const mine = cellsOf(marks, bot);
  const theirs = cellsOf(marks, opponent);

  if (mine.length === 0 && theirs.length === 0) return CORNERS.slice();

  if (mine.length === 0 && theirs.length === 1) {
    const opening = theirs[0];
    if (CORNERS.includes(opening) || EDGES.includes(opening)) return [5];
    return CORNERS.slice();
  }

  if (mine.length === 1 && CORNERS.includes(mine[0]) && theirs.length === 1) {
    if (theirs[0] === 5) {
      const opposite = OPPOSITE[mine[0]];
      if (!marks[opposite]) return [opposite];
    } else {
      const setups = CORNERS.filter(
        (cell) => !marks[cell] && between(mine[0], cell) !== theirs[0]
      );
      if (setups.length) return setups;
    }
  }

  if (!marks[5]) return [5];

  // Opposite-corner only answers their center. If we already have the center,
  // that corner is a dead diagonal — threaten through the center instead.
  if (marks[5] === bot) {
    const cornerThreats = CORNERS.filter((cell) => {
      if (marks[cell]) return false;
      const opposite = OPPOSITE[cell];
      if (marks[opposite] && marks[opposite] !== bot) return false;
      const next = { ...marks, [cell]: bot };
      return threatSquares(next, bot).length > 0;
    });
    if (cornerThreats.length) return cornerThreats;

    const forcing = forcingBlocks(marks, bot);
    if (forcing.length) return forcing;
  }

  const oppositeCorners = theirs
    .filter((cell) => CORNERS.includes(cell) && !marks[OPPOSITE[cell]])
    .map((cell) => OPPOSITE[cell]);
  if (oppositeCorners.length) return oppositeCorners;

  const freeCorners = CORNERS.filter(
    (cell) => !marks[cell] && !opponentForks.includes(cell)
  );
  if (freeCorners.length) return freeCorners;

  const freeEdges = EDGES.filter(
    (cell) => !marks[cell] && !opponentForks.includes(cell)
  );
  if (freeEdges.length) return freeEdges;

  return empties(marks);
}

const MISTAKE_RATE = 0.5;

export function chooseMove(marks, bot, rng = Math.random) {
  const options = candidateMoves(marks, bot);
  if (!options.length) return null;

  const mistakes = empties(marks).filter((cell) => !options.includes(cell));
  const pool = mistakes.length && rng() < MISTAKE_RATE ? mistakes : options;
  return pool[Math.floor(rng() * pool.length)];
}
